/**
 * Ordering a view.
 *
 * **Nothing here is optimistic**, unlike the field options next door, and the
 * difference is not a matter of taste. A sort reorders the whole table, and the
 * client holds a window of a hundred rows out of it -- so the row that belongs
 * at the top after the change may not be one of the rows on screen. Applying
 * the sort early would reorder the hundred rows the buffer happens to hold,
 * which is a different answer from the one being asked for and would be
 * replaced a moment later by the real one. The rows have to be refetched
 * whichever way this is written, so waiting for the server costs one round trip
 * and buys the only ordering that is right.
 *
 * The cached view is patched rather than invalidated. A sort's creation says
 * exactly what changed and nothing else about the view did, so re-reading every
 * view of the table to learn it would be a second round trip before the rows
 * could even be asked for. Patching bumps the row query's signature, and the
 * window refetches itself -- which is the whole mechanism, with the sort
 * standing in for the filter the plan describes.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View, ViewSort } from '@/api/types'
import { withSort, withSortOrder, withoutSort } from './view-cache'

export type SortDirection = 'ASC' | 'DESC'

export interface ViewSortMutations {
  add: (field: number, order: SortDirection) => void
  change: (sortId: number, changes: { field?: number; order?: SortDirection }) => void
  remove: (sortId: number) => void
  /** The terms in the order they should be applied, first one first. */
  prioritize: (sortIds: number[]) => void
  /** Whether a change is on its way, so the controls can say so. */
  isPending: boolean
}

export function useViewSorts(tableId: number, viewId: number): ViewSortMutations {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const store = (sort: ViewSort): void => {
    queryClient.setQueryData<View[]>(key, (views) =>
      views === undefined ? views : withSort(views, viewId, sort),
    )
  }

  const add = useMutation({
    mutationFn: (body: { field: number; order: SortDirection }): Promise<ViewSort> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/sortings/', {
          params: { path: { view_id: viewId } },
          body,
        }),
      ),
    onSuccess: store,
  })

  const change = useMutation({
    mutationFn: ({
      sortId,
      changes,
    }: {
      sortId: number
      changes: { field?: number; order?: SortDirection }
    }): Promise<ViewSort> =>
      unwrap(
        getApi().PATCH('/api/database/views/sort/{view_sort_id}/', {
          params: { path: { view_sort_id: sortId } },
          body: changes,
        }),
      ),
    onSuccess: store,
  })

  const remove = useMutation({
    mutationFn: (sortId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/views/sort/{view_sort_id}/', {
          params: { path: { view_sort_id: sortId } },
        }),
      ),
    // The response is a 204 and says nothing, so what was removed is read from
    // what was asked rather than from the answer.
    onSuccess: (_answer, sortId) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withoutSort(views, viewId, sortId),
      )
    },
  })

  const prioritize = useMutation({
    mutationFn: (ids: number[]): Promise<unknown> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/sortings/prioritize/', {
          params: { path: { view_id: viewId } },
          body: { view_sort_ids: ids },
        }),
      ),
    onMutate: (ids) => {
      // The one change here that *is* applied before the answer. Reordering the
      // terms does reorder the rows -- it bumps the row query's signature and
      // the window refetches -- but the list somebody is dragging has to follow
      // the drag, and it is the list rather than the rows that is being moved.
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withSortOrder(views, viewId, ids),
      )
      return { previous }
    },
    onError: (_error, _ids, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  return {
    add: (field, order) => {
      add.mutate({ field, order })
    },
    change: (sortId, changes) => {
      change.mutate({ sortId, changes })
    },
    remove: (sortId) => {
      remove.mutate(sortId)
    },
    prioritize: (ids) => {
      prioritize.mutate(ids)
    },
    isPending:
      add.isPending || change.isPending || remove.isPending || prioritize.isPending,
  }
}
